import type { MultiLanguage } from '@root/api/models/multi-language';

export const cloneMultiLanguage = (ml?: MultiLanguage): MultiLanguage => ({
    defaultValue: ml?.defaultValue ?? '',
    langValues: ml?.langValues ? { ...ml.langValues } : {}
});

/**
 * Resolve a translation from langValues using the requested language code,
 * with fallbacks for regional variants (e.g. `es` ↔ `es-ES`).
 */
export const resolveLangValue = (
    langValues: { [key: string]: string } | undefined,
    language?: string | null
): string | undefined => {
    if (!langValues || !language) {
        return undefined;
    }

    if (langValues[language]) {
        return langValues[language];
    }

    const normalized = language.toLowerCase();
    const baseLang = normalized.split(/[-_]/)[0];

    // `es-ES` / `es_ES` → try `es`
    if (baseLang && baseLang !== normalized && langValues[baseLang]) {
        return langValues[baseLang];
    }

    // `es` → try first matching regional key (`es-ES`, `es_MX`, ...)
    if (baseLang) {
        const regionalKey = Object.keys(langValues).find((key) => {
            const keyNormalized = key.toLowerCase();
            return keyNormalized === baseLang
                || keyNormalized.startsWith(`${baseLang}-`)
                || keyNormalized.startsWith(`${baseLang}_`);
        });
        if (regionalKey && langValues[regionalKey]) {
            return langValues[regionalKey];
        }
    }

    return undefined;
};
